/**
 * PalPendantMapPage — V2 main orchestrator for the PalPendant Locator page.
 *
 * Full-screen react-leaflet map (Google base layers) with a floating side
 * panel for searching, pinging, and live-tracking pendant devices. Polls
 * every 6 seconds for updated location data.
 *
 * Replaces V1 PalPendantMap.jsx + pendant-map-assets/Map.jsx + LocationList.jsx.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import Toast from '../../../components/v2/Toast.jsx';
import useToast from '../../../hooks/useToast.js';
import {
    getSession,
    getCampusId,
    getUserId,
    getSessionId,
    getCampusList,
    updateSessionCampus,
    getCampusTzOffset,
} from '../../../services/session.js';
import { iAmHigherAuthority } from '../../../libs.jsx';
import { fetchPendantLocations, pingPendantDevice } from './api/pendantMapApi.js';

import PendantMap from './components/PendantMap.jsx';
import LocationPanel from './components/LocationPanel.jsx';

import './palpendant-map-page.css';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const POLL_INTERVAL_MS = 6000;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const PalPendantMapPage = () => {
    // -- Session --
    const session = useMemo(() => getSession(), []);
    const campusList = useMemo(() => getCampusList() || [], []);

    const [campusId, setCampusId] = useState(() => getCampusId());


    const tzOffset = getCampusTzOffset(campusId);
    const showCampusSelector = useMemo(
        () => iAmHigherAuthority(30, session?.role_weight),
        [session]
    );

    // -- Data --
    const [locations, setLocations] = useState([]);
    const [alertedDevices, setAlertedDevices] = useState([]);
    const [loaded, setLoaded] = useState(false);

    // -- Ping --
    const [pingingDevice, setPingingDevice] = useState(null);

    // -- Toast --
    const { toast, showToast: showTypedToast, dismissToast } = useToast();
    /* This page's calls are (message, type); the shared hook is (type, message). */
    const showToast = useCallback((message, type = 'success') => showTypedToast(type, message), [showTypedToast]);

    // -- Map focus (pan/zoom + open popup target; drives PendantMap declaratively) --
    const [focus, setFocus] = useState(null);

    // ======================================================================
    // Data fetching + polling
    // ======================================================================
    const fetchData = useCallback(async () => {
        try {
            const data = await fetchPendantLocations(campusId);
            if (data && data.length > 0) {
                setLocations(data[0]?.locations ? data[0].locations : []);
                setAlertedDevices(data[0]?.alerted ? data[0].alerted : []);
            }
            setLoaded(true);
        } catch (err) {
            console.error('[PalPendantMapPage] Fetch error:', err);
        }
    }, [campusId]);

    useEffect(() => {
        // Immediate first fetch
        fetchData();

        // Poll every 6 seconds
        const interval = setInterval(fetchData, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [fetchData]);

    // ======================================================================
    // Handlers
    // ======================================================================
    const handleLocationSelect = useCallback((location) => {
        // New object identity each call so PendantMap re-flies even when the
        // same device is re-selected or a tracked device's coordinates change.
        setFocus({
            uuid: location.manufacture_uuid,
            latitude: location.latitude,
            longitude: location.longitude,
            zoom: 15,
        });
    }, []);

    const handlePingDevice = useCallback(async (deviceId) => {
        setPingingDevice(deviceId);
        try {
            const ok = await pingPendantDevice(deviceId, {
                campusId,
                userId: getUserId(),
                clientId: getSessionId(),
            });
            if (ok) {
                showToast('Device pinged successfully.');
            } else {
                showToast('Failed to ping device.', 'error');
            }
        } catch {
            showToast('Failed to ping device.', 'error');
        } finally {
            setPingingDevice(null);
        }
    }, [campusId, showToast]);

    const handleCampusChange = useCallback((newId) => {
        setCampusId(newId);
        updateSessionCampus(newId);
        setLocations([]);
        setAlertedDevices([]);
        setLoaded(false);
    }, []);

    // ======================================================================
    // Render
    // ======================================================================
    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="pm-page">
                    {/* Toast notification */}
                    <Toast toast={toast} onDismiss={dismissToast} />

                    {/* Full-screen map */}
                    <PendantMap
                        locations={locations}
                        alertedDevices={alertedDevices}
                        tzOffset={tzOffset}
                        focus={focus}
                    />

                    {/* Floating panel */}
                    <LocationPanel
                        locations={locations}
                        alertedDevices={alertedDevices}
                        onLocationSelect={handleLocationSelect}
                        campusId={campusId}
                        onCampusChange={handleCampusChange}
                        campusList={campusList}
                        showCampusSelector={showCampusSelector}
                        onPingDevice={handlePingDevice}
                        pingingDevice={pingingDevice}
                    />
                </div>
            </div>
        </>
    );
};

export default PalPendantMapPage;
